import {NextPage} from "next";
import {wrapper} from "../../app/store";
import {serverSideTranslations} from "next-i18next/serverSideTranslations";
import {StoreSectionJson, StoreSectionModel} from "../../models/storeSection";
import {StoreSectionDetailView} from "../../components/storeSectionDetail/StoreSectionDetailView";
import Bugsnag from "@bugsnag/js";
import React from "react";
import {MetaTag} from "../../models/metaTag";

interface StoreCategoryProps {
  id: string
  storeId?: string
  storeSectionType: 'category' | 'section'
  storeSectionJson?: StoreSectionJson
}

const StoreCategory: NextPage<StoreCategoryProps> = ({ id, storeSectionJson }) => {
  const storeSection = StoreSectionModel.fromJson(storeSectionJson)
  return (
    <>
      <StoreSectionDetailView sectionType="category" categoryId={id} storeSection={storeSection} />
    </>
  )
}

export async function getStaticPaths() {
  const res = await fetch(`${process.env.NEXT_PUBLIC_BASE_URL}/store/get-available-category-sections`).catch(e => {
    Bugsnag.notify(e)
    return undefined
  })
  const storeSections: StoreSectionJson[] = (await res?.json()) ?? []

  const paths = storeSections.map((storeSection) => ({
    params: {
      id: storeSection.category_id,
      storeId: storeSection.store_id,
      storeSectionType: 'category',
      storeSectionJson: storeSection
    }
  }))

  return { paths, fallback: true }
}

const createMetaTags = (params: any, storeSectionJson?: StoreSectionJson): MetaTag[] => {
  const storeSection = StoreSectionModel.fromJson(storeSectionJson)

  const metaTitle = storeSection?.title ?? null
  const metaDescription = storeSection?.subtitle ?? null
  const metaUrl = `${process.env.NEXT_PUBLIC_APP_DOMAIN_URL}/store-category/${params.id}`

  return [
    {
      property: 'og:title',
      content: metaTitle
    },
    {
      property: 'og:description',
      content: metaDescription
    },
    {
      property: 'og:url',
      content: metaUrl
    },
    {
      property: 'twitter:title',
      content: metaTitle
    },
    {
      property: 'twitter:description',
      content: metaDescription
    },
    {
      property: 'twitter:card',
      content: 'summary_large_image'
    },
  ]
}

export const getStaticProps = wrapper.getStaticProps(store => async ({ locale, params }: any) => {
  const translations = await serverSideTranslations(locale, ['common', 'landing', 'auth', 'location', 'store', 'payment', 'checkout', 'order'])
    .catch(e => {
      Bugsnag.notify(e)
    })

  if (params && params.storeSection) {
    return {
      props: {
        id: params.id,
        metaTags: createMetaTags(params, params.storeSection),
        storeSectionType: 'category',
        storeSectionJson: params.storeSection,
        ...translations,
      }
    }
  }

  const res = await fetch(`${process.env.NEXT_PUBLIC_BASE_URL}/store/get-section-for-category/${params.id}`)
    .catch(e => {
      Bugsnag.notify(e)
      return null
    })

  const storeSectionJson: StoreSectionJson | undefined = await res?.json()
    .catch(e => {
      Bugsnag.notify(e)
      return undefined
    })

  // Pass post data to the page via props
  return {
    props: {
      id: params.id,
      storeSectionType: 'category',
      metaTags: createMetaTags(params, storeSectionJson),
      storeSectionJson,
      ...translations,
    }
  }
});

export default StoreCategory